import React from "react";
import { Box, Button, CircularProgress, FormControl, MenuItem, Select, Typography } from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const ACCENT = "#26890d";
const ACTION_BLUE = "#006eff";

// Two-dropdown scope filter for the Enterprise Usage page — mirrors
// CostCenterSelector.jsx's visual pattern (used on the org-scoped dashboard)
// but with an extra "category" dropdown since this page can scope by either
// Organization or Cost Center, not just cost center within a fixed org.
const EnterpriseScopeSelector = ({ category, value, orgOptions, costCenterOptions, orgNameById = {}, loading, onCategoryChange, onValueChange, onReset }) => {
  const valueOptions = category === "org" ? orgOptions : category === "costCenter" ? costCenterOptions : [];
  const valueKey = category === "org" ? "org_id" : "cost_center_id";
  const valueLabelKey = category === "org" ? "org_name" : "cost_center_name";
  const selectedOption = valueOptions.find((o) => o[valueKey] === value) ?? null;
  const isScoped = category !== "enterprise" && Boolean(value);
  // orgOptions is usage-derived (org_breakdown) and only lists orgs with
  // activity in the current date range — an org scoped-to that has zero
  // usage (e.g. after narrowing the date filter) falls out of that list even
  // while still selected, so its name is resolved from the full active-org
  // list instead of showing "Loading…" indefinitely.
  const selectedLabel = selectedOption?.[valueLabelKey] ?? (category === "org" ? orgNameById[value] : undefined);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 2, mb: 3, backgroundColor: "#fff", border: "1px solid #e8e8e8", borderRadius: "12px", flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#555", whiteSpace: "nowrap" }}>
        Filter by
      </Typography>

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <Select
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
          sx={{ backgroundColor: "#f9f9f9", borderRadius: "6px", fontSize: "0.85rem" }}
        >
          <MenuItem value="enterprise" sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
            Enterprise (all orgs)
          </MenuItem>
          <MenuItem value="org" sx={{ fontSize: "0.85rem" }}>Organization</MenuItem>
          <MenuItem value="costCenter" sx={{ fontSize: "0.85rem" }}>Cost Center</MenuItem>
        </Select>
      </FormControl>

      {category !== "enterprise" && (
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <Select
            value={value ?? ""}
            onChange={(e) => onValueChange(e.target.value || null)}
            displayEmpty
            renderValue={(v) => {
              if (!v) return category === "org" ? "Select an organization…" : "Select a cost center…";
              return selectedLabel ?? "Loading…";
            }}
            sx={{ backgroundColor: "#f9f9f9", borderRadius: "6px", fontSize: "0.85rem" }}
          >
            <MenuItem value="" disabled sx={{ fontSize: "0.85rem" }}>
              {category === "org" ? "Select an organization…" : "Select a cost center…"}
            </MenuItem>
            {valueOptions.map((o) => (
              <MenuItem key={o[valueKey]} value={o[valueKey]} sx={{ fontSize: "0.85rem" }}>
                {o[valueLabelKey]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {loading && <CircularProgress size={18} sx={{ color: ACCENT }} />}

      {(category !== "enterprise" || value) && (
        <Button
          size="small"
          startIcon={<RestartAltIcon sx={{ fontSize: "16px !important" }} />}
          onClick={onReset}
          sx={{ textTransform: "none", fontSize: "0.8rem", fontWeight: 500, color: "#666", "&:hover": { backgroundColor: "#f0f0f0" } }}
        >
          Reset
        </Button>
      )}

      <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.6, px: 1.2, py: 0.35, backgroundColor: isScoped ? "rgba(0,110,255,0.07)" : "rgba(38,137,13,0.07)", border: `1px solid ${isScoped ? "rgba(0,110,255,0.25)" : "rgba(38,137,13,0.22)"}`, borderRadius: "20px" }}>
        <ApartmentIcon sx={{ fontSize: "13px", color: isScoped ? ACTION_BLUE : ACCENT }} />
        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: isScoped ? ACTION_BLUE : ACCENT }}>
          {isScoped
            ? `${category === "org" ? "Organization" : "Cost Center"}: ${selectedLabel ?? "Loading…"}`
            : "Enterprise-wide"}
        </Typography>
      </Box>
    </Box>
  );
};

export default EnterpriseScopeSelector;
